import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Icon,
  CircularProgress,
  Typography,
  Button,
  useTheme,
  IconButton,
} from '@material-ui/core';

import { convertObjToArr, sortArrByKey, usePrevious } from '../../../../utils';
import useStyles from './styles';

const GoNext = props => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { current, list } = useSelector(state => state.course);
  const { lessonInfos, origin } = props;
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [nextLesson, setNextLesson] = useState(null);
  const prevLessonInfos = usePrevious({ ...lessonInfos });

  const getNextLesson = useCallback(() => {
    if (!list[current.course] || !list[current.course].lessons) return;
    const lessonsObj = list[current.course].lessons;
    const lessonsArr = (() => {
      const arr = convertObjToArr(lessonsObj);
      const withModule = sortArrByKey(
        arr.filter(lesson => lesson.moduleId !== 0),
        'order',
      );
      const withoutModule = sortArrByKey(
        arr.filter(lesson => lesson.moduleId === 0),
        'order',
      );
      return withModule.concat(withoutModule);
    })();
    const nextLessonIndex = (() => {
      let nextIndex = 0;
      lessonsArr.some((lesson, index) => {
        if (lesson.id === lessonInfos.id) {
          nextIndex = index + 1;
          return true;
        }
        return false;
      });
      return nextIndex;
    })();
    setNextLesson(lessonsArr[nextLessonIndex] || false);
  }, [current, lessonInfos.id, list]);

  useEffect(() => {
    if (current && list) {
      getNextLesson();
    }
  }, [current, getNextLesson, list]);

  useEffect(() => {
    if (prevLessonInfos && Object.keys(prevLessonInfos).length > 0) {
      if (lessonInfos.id === prevLessonInfos.id) {
        if (lessonInfos.isConcluded !== prevLessonInfos.isConcluded) {
          setProgress(0);
        }
        if (
          lessonInfos.isConcluded &&
          !prevLessonInfos.isConcluded &&
          nextLesson
        ) {
          if (origin !== 'media' || lessonInfos.videoEndSkipNext) {
            setOpen(true);
          }
        }
      }
    }
  }, [lessonInfos, nextLesson, origin, prevLessonInfos]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleGoNext = useCallback(() => {
    const { pathname } = location;
    if (!nextLesson) {
      handleClose();
      return;
    }
    const targetPath = (() => {
      const path = pathname.split('/');
      path[path.length - 1] = nextLesson.id;
      return path.join('/');
    })();
    setTimeout(() => {
      handleClose();
      history.push(targetPath);
    }, 1000);
  }, [handleClose, history, location, nextLesson]);

  const getProgressToGo = useCallback(
    type => {
      const seconds = 5;
      switch (type) {
        case 'seconds':
          return seconds - progress / (100 / seconds);
        case 'percentage':
        default:
          return progress + 100 / seconds;
      }
    },
    [progress],
  );

  useEffect(() => {
    if (!open) return;
    if (progress < 100) {
      setTimeout(() => setProgress(getProgressToGo('percentage')), 1000);
    } else {
      handleGoNext();
    }
  }, [progress, open, getProgressToGo, handleGoNext]);

  return (
    <Dialog open={open} onClose={handleClose} className={classes.container}>
      <DialogContent>
        <Box
          className={`progress-container ${
            progress === 100 ? 'completed' : ''
          }`}
        >
          <CircularProgress
            className="progress-chart"
            variant="static"
            value={progress}
            size={theme.spacing(9)}
            color="inherit"
          />
          <IconButton
            className="progress-button"
            onClick={handleClose}
            disabled={progress === 100}
            size="medium"
            disableRipple
          >
            <Typography className="progress-button--text" variant="h6">
              {getProgressToGo('seconds')}
            </Typography>
            <Icon className="progress-button--icon" fontSize="large">
              {progress === 100 ? 'check' : 'skip_next'}
            </Icon>
          </IconButton>
        </Box>
        <Typography className="title">
          Redirecionando para próxima aula...
        </Typography>
      </DialogContent>
      <DialogActions className="actions-container">
        <Button onClick={handleClose} color="primary">
          Permanecer aqui
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoNext;
